import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, ChevronDown, FileText, Star } from "lucide-react";
import { cvList } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const trackCvDownload = (slug) => {
  try {
    const body = JSON.stringify({ slug });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${API}/cv-download`, new Blob([body], { type: "application/json" }));
    } else {
      fetch(`${API}/cv-download`, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true });
    }
  } catch (e) { /* silent */ }
};

// variant: "primary" | "outline" | "nav" | "block"
export const CVMenu = ({ variant = "primary", label = "Télécharger mon CV", testid = "cv-menu", align = "left" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);


  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const triggerClass =
    variant === "primary"
      ? "inline-flex items-center gap-2 bg-cyan text-[#05070A] font-medium px-6 py-3.5 rounded-full hover:bg-white transition-colors duration-300"
      : variant === "outline"
      ? "inline-flex items-center gap-2 border border-white/15 text-white px-6 py-3.5 rounded-full hover:border-cyan/60 hover:bg-white/5 transition-colors duration-300"
      : variant === "nav"
      ? "inline-flex items-center gap-2 text-sm bg-cyan text-[#05070A] font-medium px-4 py-2 rounded-full hover:bg-white transition-colors duration-300"
      : "w-full flex items-center justify-between border border-white/10 rounded-xl px-5 py-4 hover:border-cyan/40 transition-colors duration-300 text-white/90";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={triggerClass}
        data-testid={`${testid}-trigger`}
        aria-haspopup="true"
        aria-expanded={open}
      >
        {variant === "block" ? (
          <span className="flex items-center gap-3"><Download size={18} className="text-cyan" /> {label}</span>
        ) : (
          <>{label}</>
        )}
        <ChevronDown size={variant === "nav" ? 14 : 16} className={`transition-transform duration-300 ${open ? "rotate-180" : ""} ${variant === "block" ? "text-[var(--text-2)]" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className={`absolute z-50 mt-2 w-72 glass border border-white/10 rounded-2xl p-2 shadow-2xl shadow-black/60 ${align === "right" ? "right-0" : "left-0"}`}
            data-testid={`${testid}-list`}
          >
            <p className="px-3 pt-2 pb-1.5 text-[10px] uppercase tracking-[0.25em] text-[var(--muted)]">Choisir selon vos enjeux</p>
            {cvList.map((cv, i) => (
              <a
                key={i}
                href={cv.file}
                download={cv.download}
                onClick={() => { trackCvDownload(cv.slug); setOpen(false); }}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-white/5 transition-colors duration-200 group"
                data-testid={`${testid}-item-${i}`}
              >
                <FileText size={16} className="text-cyan shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="text-sm text-white/90 group-hover:text-white">{cv.axe}</span>
                    {cv.recommended && (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-cyan bg-cyan/10 border border-cyan/30 rounded-full px-1.5 py-0.5">
                        <Star size={8} className="fill-cyan" /> Recommandé
                      </span>
                    )}
                  </span>
                </span>
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
